import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import FamilyService from '../../services/familyService';

interface HealthEvent {
  id: string;
  type: 'positive' | 'negative';
  description: string;
  date: string;
  category: string;
  icon: string;
}

interface HealthHistoryModalProps {
  isOpen: boolean;
  memberId: string;
  memberName: string;
  onClose: () => void;
}

export default function HealthHistoryModal({ isOpen, memberId, memberName, onClose }: HealthHistoryModalProps) {
  const [events, setEvents] = useState<HealthEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterType, setFilterType] = useState<'all' | 'positive' | 'negative'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterDate, setFilterDate] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  const LIMIT = 20;

  const familyService = useMemo(() => FamilyService.getInstance(), []);

  const since = useMemo(() => {
    if (filterDate === 'all') return undefined as string | undefined;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (filterDate === 'today') return today.toISOString();
    if (filterDate === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return weekAgo.toISOString();
    }
    if (filterDate === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      return monthAgo.toISOString();
    }
    return undefined;
  }, [filterDate]);

  // Rótulos em PT-BR para as categorias
  const getCategoryLabel = (category: string): string => {
    switch (category) {
      case 'treatment':
        return 'Tratamento';
      case 'illness':
        return 'Doença';
      case 'allopathic':
        return 'Alopático';
      case 'medical_record':
        return 'Registro Médico';
      case 'badge':
        return 'Conquista';
      case 'level':
        return 'Nível';
      case 'general':
        return 'Geral';
      default:
        return category?.charAt(0).toUpperCase() + category?.slice(1);
    }
  };

  // Normaliza rótulo para doses perdidas (mostrar "Dose perdida ou muito fora do horário")
  const normalizeMissedDoseDescription = (desc: string): string => {
    if (!desc) return desc;
    // Substitui início "Dose perdida" e também casos já mapeados com "/" ou "ou"
    return desc.replace(/^Dose perdida(?:\s*(?:\/|ou)\s*muito fora do horário)?\b/i, 'Dose perdida ou muito fora do horário');
  };

  const loadEvents = useCallback(async (reset: boolean = false) => {
    if (reset) {
      setLoading(true);
      setEvents([]);
      offsetRef.current = 0;
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const data = await familyService.getMemberHealthJournal(memberId, {
        limit: LIMIT,
        offset: offsetRef.current,
        type: filterType,
        category: filterCategory,
        since
      });

      const formatted: HealthEvent[] = (data || []).map((event: any) => ({
        id: event.id,
        type: event.event_type as 'positive' | 'negative',
        description: normalizeMissedDoseDescription(event.description),
        date: new Date(event.created_at).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
        category: event.category || 'general',
        icon: ''
      }));

      if (formatted.length === 0 && reset) {
        // Fallback para tabela legacy health_events se a view unificada não existir ou estiver vazia
        const legacy = await familyService.getMemberHealthEvents(memberId);
        const legacyFormatted: HealthEvent[] = (legacy || []).map((e: any) => ({
          id: e.id,
          type: (e.event_type as 'positive' | 'negative') ?? 'positive',
          description: normalizeMissedDoseDescription(e.description),
          date: new Date(e.created_at).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }),
          category: e.category || 'general',
          icon: ''
        }));
        setEvents(legacyFormatted);
        setHasMore(false);
        offsetRef.current = legacyFormatted.length;
      } else {
        if (reset) {
          setEvents(formatted);
        } else {
          setEvents(prev => [...prev, ...formatted]);
        }
        const newOffset = offsetRef.current + formatted.length;
        offsetRef.current = newOffset;
        setHasMore(formatted.length === LIMIT);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar histórico:', error);
      if (reset) setEvents([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [familyService, memberId, filterType, filterCategory, since]);

  useEffect(() => {
    if (isOpen) {
      void loadEvents(true);
    }
  }, [isOpen, loadEvents]);

  // Removido efeito duplicado: o efeito acima já depende de loadEvents,
  // que muda quando filtros (filterType/category/since) mudam.

  const getCategoryIconColor = (category: string, type: 'positive' | 'negative', description?: string) => {
    if (type === 'negative') {
      return 'bg-red-100 text-red-500';
    }
    
    switch (category) {
      case 'treatment':
        // Verde para dose tomada no horário
        if (description && description.toLowerCase().startsWith('dose tomada no horário')) {
          return 'bg-emerald-100 text-emerald-600';
        }
        // Amarelo para outras doses positivas (ex.: fora do horário)
        return 'bg-orange-100 text-orange-500';
      case 'illness':
        return 'bg-emerald-100 text-emerald-500';
      case 'allopathic':
        return 'bg-blue-100 text-blue-500';
      case 'medical_record':
        return 'bg-red-100 text-red-500';
      case 'badge':
        return 'bg-yellow-100 text-yellow-600';
      case 'level':
        return 'bg-blue-100 text-blue-600';
      default:
        return 'bg-emerald-100 text-emerald-500';
    }
  };

  const getCategoryIcon = (category: string, type: 'positive' | 'negative') => {
    if (type === 'negative') {
      return 'ri-alert-line';
    }
    
    switch (category) {
      case 'treatment':
        return 'ri-medicine-bottle-line';
      case 'illness':
        return 'ri-shield-check-line';
      case 'allopathic':
        return 'ri-medicine-bottle-fill';
      case 'medical_record':
        return 'ri-health-book-line';
      case 'badge':
        return 'ri-award-fill';
      case 'level':
        return 'ri-arrow-up-circle-fill';
      default:
        return 'ri-heart-fill';
    }
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) void loadEvents(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-emerald-500 to-blue-500 p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-2xl font-bold">Histórico de Saúde</h3>
              <p className="text-white/80 text-sm mt-1">{memberName}</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {/* Filtros */}
          <div className="space-y-3">
            {/* Filtro por Tipo */}
            <div>
              <label className="block text-sm font-medium mb-2">Tipo de Evento</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilterType('all')}
                  className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-colors ${
                    filterType === 'all'
                      ? 'bg-white text-emerald-600'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  Todos
                </button>
                <button
                  onClick={() => setFilterType('positive')}
                  className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-colors ${
                    filterType === 'positive'
                      ? 'bg-white text-emerald-600'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  <i className="ri-heart-fill mr-1"></i>
                  Positivos
                </button>
                <button
                  onClick={() => setFilterType('negative')}
                  className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-colors ${
                    filterType === 'negative'
                      ? 'bg-white text-red-600'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  <i className="ri-alert-line mr-1"></i>
                  Negativos
                </button>
              </div>
            </div>

            {/* Filtro por Categoria */}
            <div>
              <label className="block text-sm font-medium mb-2">Categoria</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full py-2 px-4 rounded-xl bg-white/20 text-white border border-white/30 focus:outline-none focus:border-white"
              >
                <option value="all" className="text-gray-800">Todas as categorias</option>
                <option value="general" className="text-gray-800">Geral</option>
                <option value="treatment" className="text-gray-800">Tratamento</option>
                <option value="illness" className="text-gray-800">Doença</option>
                <option value="allopathic" className="text-gray-800">Alopático</option>
                <option value="medical_record" className="text-gray-800">Registro Médico</option>
                <option value="badge" className="text-gray-800">Conquista</option>
                <option value="level" className="text-gray-800">Nível</option>
              </select>
            </div>

            {/* Filtro por Período */}
            <div>
              <label className="block text-sm font-medium mb-2">Período</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilterDate('all')}
                  className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-colors ${
                    filterDate === 'all'
                      ? 'bg-white text-emerald-600'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  Tudo
                </button>
                <button
                  onClick={() => setFilterDate('today')}
                  className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-colors ${
                    filterDate === 'today'
                      ? 'bg-white text-emerald-600'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  Hoje
                </button>
                <button
                  onClick={() => setFilterDate('week')}
                  className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-colors ${
                    filterDate === 'week'
                      ? 'bg-white text-emerald-600'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  Semana
                </button>
                <button
                  onClick={() => setFilterDate('month')}
                  className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-colors ${
                    filterDate === 'month'
                      ? 'bg-white text-emerald-600'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  Mês
                </button>
              </div>
            </div>
          </div>

          {/* Contador */}
          <div className="mt-4 text-center">
            <p className="text-white/90 text-sm">{events.length} {events.length === 1 ? 'evento encontrado' : 'eventos encontrados'}</p>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : events.length > 0 ? (
            <div className="space-y-3">
              {events.map((event) => (
                <div
                  key={event.id}
                  className="bg-gray-50 rounded-2xl p-4 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-start">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${getCategoryIconColor(event.category, event.type, event.description)}`}>
                      <i className={`${event.icon || getCategoryIcon(event.category, event.type)} text-lg`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <p className="text-base font-medium text-gray-800">{event.description}</p>
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                          event.type === 'positive' 
                            ? 'bg-emerald-100 text-emerald-600' 
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {event.type === 'positive' ? 'Positivo' : 'Negativo'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{event.date}</p>
                      <p className="text-xs text-gray-400 mt-1">{getCategoryLabel(event.category)}</p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Botão Carregar Mais */}
              {!loading && hasMore && (
                <div className="flex justify-center pt-4">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="px-6 py-3 bg-emerald-500 text-white font-medium rounded-2xl hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loadingMore ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Carregando...</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-arrow-down-line text-xl"></i>
                        <span>Carregar mais</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                <i className="ri-heart-pulse-line text-gray-400 text-3xl"></i>
              </div>
              <p className="text-gray-500 text-base font-medium">Nenhum evento encontrado</p>
              <p className="text-gray-400 text-sm mt-1">
                {filterType !== 'all' || filterCategory !== 'all'
                  ? 'Tente ajustar os filtros'
                  : 'Adicione eventos usando "Como me sinto hoje"'}
              </p>
            </div>
          )}
        </div>

        {/* Rodapé removido: paginação agora é server-side com botão Carregar Mais */}
      </div>
    </div>
  );
}
